import { useState, useEffect, useCallback } from 'react';
import { plansApi } from '../../../services/api';
import type { PlanListItem } from './types';
import { 
  Button, 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogFooter, 
  DialogTitle, 
  DialogDescription,
} from '@/components/ui';
import { logger } from '../../../utils/logger';

interface PlansTabProps {
  projectId: string;
  onOpenDocument: (doc: {
    id: string;
    documentNumber?: string;
    projectId?: string;
    title: string;
    type: 'project' | 'company' | 'standard' | 'prior-project';
    canEdit?: boolean;
    status?: string;
    currentRevision?: string;
  }) => void;
}

export function PlansTab({ projectId, onOpenDocument }: PlansTabProps) {
  const [plans, setPlans] = useState<PlanListItem[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [showDeletePlanModal, setShowDeletePlanModal] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<PlanListItem | null>(null);
  const [isDeletingPlan, setIsDeletingPlan] = useState(false);

  const loadPlans = useCallback(async () => {
    setLoadingPlans(true);
    try {
      const plansList = await plansApi.list(projectId);
      setPlans(plansList || []);
    } catch (err) {
      logger.error('Failed to load plans:', err);
      setPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleDeletePlan = useCallback(async () => {
    if (!planToDelete) return;
    
    setIsDeletingPlan(true);
    try {
      await plansApi.delete(projectId, planToDelete.documentNumber);
      setShowDeletePlanModal(false);
      setPlanToDelete(null);
      loadPlans();
    } catch (err) {
      logger.error('Failed to delete plan:', err);
    } finally {
      setIsDeletingPlan(false);
    }
  }, [projectId, planToDelete, loadPlans]);

  return (
    <div className="space-y-4">
      {/* Header with create button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Plans ({plans.length})</h3>
        <Button
          onClick={async () => {
            const planNumber = `PLAN-${Date.now().toString(36).toUpperCase()}`;
            const planTitle = 'New Plan';
            try {
              await plansApi.create(projectId, {
                documentNumber: planNumber,
                documentTitle: planTitle,
              });
              loadPlans();
              
              onOpenDocument({
                id: planNumber,
                documentNumber: planNumber,
                projectId: projectId,
                title: planTitle,
                type: 'project',
                canEdit: true,
                status: 'Draft',
              });
            } catch (err) {
              logger.error('Failed to create plan:', err);
            }
          }}
          size="sm"
        >
          + New Plan
        </Button>
      </div>
      
      {loadingPlans ? (
        <div className="text-center py-8">
          <div className="animate-spin text-2xl">⏳</div>
        </div>
      ) : plans.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <div className="text-4xl mb-3">📋</div>
          <p className="text-lg font-medium">No plans yet</p>
          <p className="text-sm mt-1">
            Create a plan to organize multi-step tasks across documents.
          </p>
          <p className="text-xs text-muted-foreground mt-4">
            Plans help you and the AI collaborate on complex document updates.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {plans.map(plan => (
            <div
              key={plan.documentNumber}
              className="p-3 bg-muted/30 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
              onClick={() => {
                onOpenDocument({
                  id: plan.documentNumber,
                  documentNumber: plan.documentNumber,
                  projectId: projectId,
                  title: plan.documentTitle,
                  type: 'project',
                  canEdit: true,
                  status: 'Draft',
                });
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-info">
                    {plan.documentNumber}
                  </span>
                  <p className="text-xs text-muted-foreground truncate">
                    {plan.documentTitle}
                  </p>
                  {plan.tasks && plan.tasks.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {plan.tasks.filter(t => t.status === 'completed').length}/{plan.tasks.length} tasks completed
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    plan.planStatus === 'completed' ? 'bg-success-light text-success' :
                    plan.planStatus === 'executing' ? 'bg-warning-light text-warning' :
                    plan.planStatus === 'ready' ? 'bg-info-light text-info' :
                    plan.planStatus === 'failed' || plan.planStatus === 'partial' ? 'bg-destructive-light text-destructive' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {plan.planStatus}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPlanToDelete(plan);
                      setShowDeletePlanModal(true);
                    }}
                    title="Delete plan"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Plan Modal */}
      <Dialog open={showDeletePlanModal && !!planToDelete} onOpenChange={(open) => {
        if (!open) {
          setShowDeletePlanModal(false);
          setPlanToDelete(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Plan</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this plan? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {planToDelete && (
            <div className="space-y-4 py-4">
              <div className="bg-muted/30 rounded-lg p-3 text-sm space-y-2">
                <div>
                  <strong>Plan:</strong> {planToDelete.documentNumber}
                </div>
                <div>
                  <strong>Title:</strong> {planToDelete.documentTitle}
                </div>
                <div>
                  <strong>Status:</strong> {planToDelete.planStatus || 'draft'}
                </div>
                {planToDelete.tasks && planToDelete.tasks.length > 0 && (
                  <div>
                    <strong>Tasks:</strong> {planToDelete.tasks.length}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => {
                setShowDeletePlanModal(false);
                setPlanToDelete(null);
              }}
              variant="outline"
              disabled={isDeletingPlan}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeletePlan}
              disabled={isDeletingPlan}
              variant="destructive"
            >
              {isDeletingPlan ? 'Deleting...' : 'Delete Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
